/**
 * One day-bucketed chart on the AI Service Analytics tab (task 1255).
 *
 * The app has no charting library, and this tab adds none: like the testing
 * trend dots, the marks are plain SVG styled with Tailwind. The rules are the
 * platform dataviz ones:
 *
 *   - **Color by slot, in fixed order.** `ANALYTICS_SERIES_SLOTS` is the
 *     validated categorical palette, a light step and a dark step per slot. The
 *     app's indigo stays an interface accent and is never a series color, and
 *     text never wears a series color.
 *   - **One axis.** A second measure (tokens on the cost chart) goes in the
 *     readout, never on a second scale.
 *   - **Thin marks.** Bars with 4px rounded ends at the top, a 2px surface gap
 *     between stacked segments, 2px lines, an 8px marker only at the hovered day.
 *   - **Hover readout by default.** A column-wide hit target per day shows every
 *     series at that day plus its annotations; clicking it drills down. The
 *     readout sits 16px beside the pointer, never over the hovered column, and
 *     flips to the pointer's left near the right edge (task 1304).
 *   - **Linked hover.** The tab passes one hovered day to every chart, so a day
 *     hovered on one chart is marked, with its readout, on all of them; a
 *     chart not under the pointer puts its readout beside its own column.
 *   - **A table twin.** Every chart switches to a table of the same numbers,
 *     each day linking to its runs, which is also the keyboard path to the
 *     drill-down.
 *   - **Annotations on every chart.** Publish and release events are a vertical
 *     hairline at their day, labelled where there is room and always listed in
 *     that day's readout. Labels that would collide drop to a second row, and
 *     one that collides there too keeps only its hairline.
 */
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AnalyticsAnnotation } from '@/types/analytics';
import { formatDay } from './analyticsFormat';

/**
 * The categorical slots, validated on the app's card surfaces (white in light
 * mode, gray-800 in dark): slot 1 blue, 2 orange, 3 aqua, 4 yellow, 5 magenta,
 * 6 green, 7 violet, 8 red. Literal class strings so Tailwind keeps them.
 */
export const ANALYTICS_SERIES_SLOTS = [
  { fill: 'fill-[#2a78d6] dark:fill-[#3987e5]', stroke: 'stroke-[#2a78d6] dark:stroke-[#3987e5]', swatch: 'bg-[#2a78d6] dark:bg-[#3987e5]' },
  { fill: 'fill-[#eb6834] dark:fill-[#d95926]', stroke: 'stroke-[#eb6834] dark:stroke-[#d95926]', swatch: 'bg-[#eb6834] dark:bg-[#d95926]' },
  { fill: 'fill-[#1baf7a] dark:fill-[#199e70]', stroke: 'stroke-[#1baf7a] dark:stroke-[#199e70]', swatch: 'bg-[#1baf7a] dark:bg-[#199e70]' },
  { fill: 'fill-[#eda100] dark:fill-[#c98500]', stroke: 'stroke-[#eda100] dark:stroke-[#c98500]', swatch: 'bg-[#eda100] dark:bg-[#c98500]' },
  { fill: 'fill-[#e87ba4] dark:fill-[#d55181]', stroke: 'stroke-[#e87ba4] dark:stroke-[#d55181]', swatch: 'bg-[#e87ba4] dark:bg-[#d55181]' },
  { fill: 'fill-[#008300] dark:fill-[#008300]', stroke: 'stroke-[#008300] dark:stroke-[#008300]', swatch: 'bg-[#008300] dark:bg-[#008300]' },
  { fill: 'fill-[#6250d6] dark:fill-[#9085e9]', stroke: 'stroke-[#6250d6] dark:stroke-[#9085e9]', swatch: 'bg-[#6250d6] dark:bg-[#9085e9]' },
  { fill: 'fill-[#e34948] dark:fill-[#e66767]', stroke: 'stroke-[#e34948] dark:stroke-[#e66767]', swatch: 'bg-[#e34948] dark:bg-[#e66767]' },
] as const;

export interface DailySeries {
  key: string;
  label: string;
  /** 0-based index into `ANALYTICS_SERIES_SLOTS`; follows the entity, never its rank. */
  slot: number;
  values: (number | null)[];
}

interface DailyChartProps {
  title: string;
  subtitle?: string;
  testId: string;
  dates: string[];
  /** `bar` stacks the series in order, bottom up; `line` draws one line each. */
  kind: 'bar' | 'line';
  series: DailySeries[];
  annotations: AnalyticsAnnotation[];
  formatValue: (n: number) => string;
  /** A fixed top for the y axis (1 for a share); otherwise read off the data. */
  yMax?: number;
  /** The values are counts, so every axis label must be a whole number. */
  integerAxis?: boolean;
  /** Extra readout lines for a day, below the series values. */
  readoutExtra?: (index: number) => ReactNode;
  /** Extra table columns, one cell per day. */
  tableExtra?: { header: string; cell: (index: number) => ReactNode };
  /** Where the day drills down to. */
  dayLink: (index: number) => string;
  /** Rendered under the chart (the Errors chart's top messages). */
  children?: ReactNode;
  /**
   * Join a line across days with no value. For a series that is sparse by
   * nature (criteria are scored only on days a test ran), where each point is
   * an observation and the days between are not a drop.
   */
  connectGaps?: boolean;
  /**
   * The day hovered anywhere on the tab, shared by every chart (task 1304).
   * With `onHover` the chart is controlled; without, it keeps its own.
   */
  hoveredDay?: number | null;
  onHover?: (index: number | null) => void;
}

const HEIGHT = 200;
const PAD = { top: 22, right: 12, bottom: 22, left: 56 };
/** Room above the plot for two rows of annotation labels. */
const ANNOTATED_TOP = 36;
const FALLBACK_WIDTH = 720;
/** The readout's gap from the pointer, and its min width (min-w-[10rem]) until measured. */
const READOUT_GAP = 16;
const READOUT_MIN_WIDTH = 160;
/** The 10px annotation label's width per character, an estimate as in the design file. */
const LABEL_CHAR_W = 5.6;
const LABEL_MAX_CHARS = 28;
/** Labels closer than this to the previous one on a row collide. */
const LABEL_GAP = 6;

/** The container's width, read once and on resize; jsdom reads the fallback. */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.clientWidth > 0) setWidth(el.clientWidth);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * A ceiling that reads well on an axis: 1, 2, 2.5 or 5 times a power of ten.
 * A count axis also needs a whole-number midpoint, so it never offers 2.5 and
 * rounds an odd ceiling up to the next even one.
 */
function niceCeiling(max: number, integer: boolean): number {
  if (!(max > 0)) return integer ? 2 : 1;
  const power = 10 ** Math.floor(Math.log10(max));
  let top = 10 * power;
  for (const step of integer ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10]) {
    if (step * power >= max) {
      top = step * power;
      break;
    }
  }
  return integer ? Math.max(2, Math.ceil(top / 2) * 2) : top;
}

/** A bar with its top corners rounded and its base square on the baseline. */
function barPath(x: number, y: number, w: number, h: number, rounded: boolean): string {
  const r = rounded ? Math.min(4, w / 2, h) : 0;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/** A line's points split at missing days, so a gap is a gap and not a slope. */
function lineSegments(points: ({ x: number; y: number } | null)[]): { x: number; y: number }[][] {
  const segments: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  for (const p of points) {
    if (p) current.push(p);
    else if (current.length) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length) segments.push(current);
  return segments;
}

export function DailyChart({
  title,
  subtitle,
  testId,
  dates,
  kind,
  series,
  annotations,
  formatValue,
  yMax,
  readoutExtra,
  tableExtra,
  dayLink,
  children,
  connectGaps = false,
  integerAxis = false,
  hoveredDay,
  onHover,
}: DailyChartProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [localHover, setLocalHover] = useState<number | null>(null);
  const hover = onHover ? (hoveredDay ?? null) : localHover;
  const setHover = onHover ?? setLocalHover;
  /** Where the pointer is within the plot, while it is over this chart. */
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const readoutRef = useRef<HTMLDivElement>(null);
  const [readoutWidth, setReadoutWidth] = useState(READOUT_MIN_WIDTH);
  const [ref, width] = useWidth();

  const n = dates.length;
  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const band = n > 0 ? plotW / n : plotW;
  const xCenter = (i: number) => PAD.left + band * i + band / 2;

  const annotationsByDay = new Map<number, AnalyticsAnnotation[]>();
  for (const a of annotations) {
    const index = dates.indexOf(a.date);
    if (index === -1) continue;
    annotationsByDay.set(index, [...(annotationsByDay.get(index) ?? []), a]);
  }
  const padTop = annotationsByDay.size > 0 ? ANNOTATED_TOP : PAD.top;
  const plotH = HEIGHT - padTop - PAD.bottom;

  // Lay the labels out left to right, as the design file's annotate(): a label
  // that would start within 6px of the previous one's end on the first row
  // drops to the second; one that collides there too keeps only its hairline
  // (the day's readout still lists it). A label that would run past the plot's
  // right edge is drawn to the left of its line.
  const rowEnds = [-Infinity, -Infinity];
  const annotationMarks = [...annotationsByDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, items]) => {
      const x = xCenter(index);
      const joined = items.map((a) => a.label).join(' · ');
      const text = joined.length > LABEL_MAX_CHARS ? `${joined.slice(0, LABEL_MAX_CHARS - 1)}…` : joined;
      const labelWidth = text.length * LABEL_CHAR_W;
      const anchorEnd = x + 3 + labelWidth > PAD.left + plotW;
      const start = anchorEnd ? x - 3 - labelWidth : x + 3;
      const row = rowEnds.findIndex((end) => start >= end + LABEL_GAP);
      if (row !== -1) rowEnds[row] = start + labelWidth;
      return { index, x, items, text, anchorEnd, row: row === -1 ? null : row };
    });

  const dataMax =
    kind === 'bar'
      ? Math.max(0, ...dates.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0)))
      : Math.max(0, ...series.flatMap((s) => s.values.map((v) => v ?? 0)));
  const top = yMax ?? niceCeiling(dataMax, integerAxis);
  const y = (v: number) => padTop + plotH - (Math.min(v, top) / top) * plotH;

  const xTicks = n === 0 ? [] : [...new Set([0, Math.floor((n - 1) / 2), n - 1])];
  const barWidth = Math.max(1, Math.min(band - 2, band * 0.72));

  const hovered = hover !== null && hover < n ? hover : null;

  // Measure the readout once it is drawn so the edge flip uses its real width.
  useLayoutEffect(() => {
    const measured = readoutRef.current?.offsetWidth ?? 0;
    if (hovered !== null && measured > 0 && measured !== readoutWidth) setReadoutWidth(measured);
  });

  // Beside the pointer on the chart under it; beside the hovered column, at the
  // top of the plot, on every other chart. Either way 16px to the right, or to
  // the left when the right side would run past the card.
  const anchorX = hovered === null ? null : pointer ? pointer.x : xCenter(hovered);
  const readoutPosition = anchorX !== null && {
    left: anchorX + READOUT_GAP + readoutWidth > width ? anchorX - READOUT_GAP - readoutWidth : anchorX + READOUT_GAP,
    top: pointer ? Math.max(0, pointer.y - 12) : padTop,
  };

  const trackPointer = (event: ReactMouseEvent) => {
    const box = plotRef.current?.getBoundingClientRect();
    if (box) setPointer({ x: event.clientX - box.left, y: event.clientY - box.top });
  };

  return (
    <section
      data-testid={testId}
      className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300 text-xs dark:border-gray-600">
          {(['chart', 'table'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={`px-2.5 py-1 ${
                view === v
                  ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {v === 'chart' ? 'Chart' : 'Table'}
            </button>
          ))}
        </div>
      </div>

      {series.length >= 2 && (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300" aria-label={`${title} legend`}>
          {series.map((s) => (
            <li key={s.key} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={`inline-block ${kind === 'bar' ? 'h-2.5 w-2.5 rounded-sm' : 'h-0.5 w-3.5 rounded'} ${
                  ANALYTICS_SERIES_SLOTS[s.slot % ANALYTICS_SERIES_SLOTS.length].swatch
                }`}
              />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      <div ref={ref}>
      {view === 'chart' ? (
        <div
          ref={plotRef}
          className="relative mt-2"
          onMouseLeave={() => {
            setPointer(null);
            setHover(null);
          }}
        >
          <svg width={width} height={HEIGHT} role="img" aria-label={`${title}, ${n} days`} className="block max-w-full">
            {/* Recessive grid: the baseline, the middle and the top. */}
            {[0, 0.5, 1].map((t) => (
              <g key={t}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={y(top * t)}
                  y2={y(top * t)}
                  className={t === 0 ? 'stroke-gray-300 dark:stroke-gray-600' : 'stroke-gray-100 dark:stroke-gray-700'}
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 6}
                  y={y(top * t)}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-gray-500 text-[11px] dark:fill-gray-400"
                >
                  {formatValue(top * t)}
                </text>
              </g>
            ))}
            {xTicks.map((i) => (
              <text
                key={i}
                x={xCenter(i)}
                y={HEIGHT - 6}
                textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'}
                className="fill-gray-500 text-[11px] dark:fill-gray-400"
              >
                {formatDay(dates[i])}
              </text>
            ))}

            {/* The hovered day's column. */}
            {hovered !== null && (
              <rect
                x={PAD.left + band * hovered}
                y={padTop}
                width={band}
                height={plotH}
                className="fill-gray-100 dark:fill-gray-700/60"
              />
            )}

            {kind === 'bar' &&
              dates.map((date, i) => {
                let base = 0;
                const stacked = series
                  .map((s) => ({ s, v: s.values[i] ?? 0 }))
                  .filter((seg) => seg.v > 0);
                return (
                  <g key={date}>
                    {stacked.map((seg, k) => {
                      const y0 = y(base);
                      const y1 = y(base + seg.v);
                      base += seg.v;
                      // 2px surface gap between stacked segments.
                      const h = Math.max(y0 - y1 - (k > 0 ? 2 : 0), 1);
                      return (
                        <path
                          key={seg.s.key}
                          d={barPath(xCenter(i) - barWidth / 2, y1, barWidth, h, k === stacked.length - 1)}
                          className={ANALYTICS_SERIES_SLOTS[seg.s.slot % ANALYTICS_SERIES_SLOTS.length].fill}
                        />
                      );
                    })}
                  </g>
                );
              })}

            {kind === 'line' &&
              series.map((s) => {
                const slot = ANALYTICS_SERIES_SLOTS[s.slot % ANALYTICS_SERIES_SLOTS.length];
                const points = s.values.map((v, i) =>
                  v === null || v === undefined ? null : { x: xCenter(i), y: y(v) }
                );
                const segments = lineSegments(connectGaps ? points.filter(Boolean) : points);
                return (
                  <g key={s.key}>
                    {segments.map((seg, k) =>
                      seg.length === 1 ? (
                        <circle key={k} cx={seg[0].x} cy={seg[0].y} r={3} className={slot.fill} />
                      ) : (
                        <polyline
                          key={k}
                          points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
                          fill="none"
                          strokeWidth={2}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          className={slot.stroke}
                        />
                      )
                    )}
                    {hovered !== null && s.values[hovered] !== null && s.values[hovered] !== undefined && (
                      <circle
                        cx={xCenter(hovered)}
                        cy={y(s.values[hovered] as number)}
                        r={4}
                        strokeWidth={2}
                        className={`${slot.fill} stroke-white dark:stroke-gray-800`}
                      />
                    )}
                  </g>
                );
              })}

            {/* Publish and release events: a hairline and, where there is room, a label on one of two rows. */}
            {annotationMarks.map((mark) => {
              const labelY = mark.row === 1 ? padTop - 6 : padTop - 19;
              return (
                <g key={mark.index} data-testid={`${testId}-annotation`}>
                  <line
                    x1={mark.x}
                    x2={mark.x}
                    y1={mark.row === null ? padTop - 4 : labelY + 3}
                    y2={padTop + plotH}
                    strokeWidth={1}
                    className="stroke-gray-500 dark:stroke-gray-400"
                  />
                  {mark.row !== null && (
                    <text
                      x={mark.anchorEnd ? mark.x - 3 : mark.x + 3}
                      y={labelY}
                      textAnchor={mark.anchorEnd ? 'end' : 'start'}
                      data-row={mark.row}
                      className="fill-gray-700 text-[10px] font-medium dark:fill-gray-300"
                    >
                      {mark.text}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Column-wide hit targets: wider than any mark, one per day. */}
            {dates.map((date, i) => (
              <rect
                key={date}
                data-testid={`${testId}-day-${date}`}
                x={PAD.left + band * i}
                y={padTop}
                width={band}
                height={plotH}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHover(i)}
                onMouseMove={(event) => {
                  trackPointer(event);
                  if (hovered !== i) setHover(i);
                }}
                onClick={() => navigate(dayLink(i))}
              />
            ))}
          </svg>

          {hovered !== null && readoutPosition && (
            <div
              ref={readoutRef}
              role="status"
              data-testid={`${testId}-readout`}
              className="pointer-events-none absolute z-10 min-w-[10rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-600 dark:bg-gray-900"
              style={{ left: `${readoutPosition.left}px`, top: `${readoutPosition.top}px` }}
            >
              <div className="font-semibold text-gray-900 dark:text-gray-100">{formatDay(dates[hovered])}</div>
              {series.map((s) => (
                <div key={s.key} className="mt-0.5 flex items-center justify-between gap-3 text-gray-600 dark:text-gray-300">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className={`inline-block h-2 w-2 rounded-sm ${ANALYTICS_SERIES_SLOTS[s.slot % ANALYTICS_SERIES_SLOTS.length].swatch}`}
                    />
                    {s.label}
                  </span>
                  <span className="whitespace-nowrap font-medium text-gray-900 tabular-nums dark:text-gray-100">
                    {s.values[hovered] === null || s.values[hovered] === undefined ? '—' : formatValue(s.values[hovered] as number)}
                  </span>
                </div>
              ))}
              {readoutExtra?.(hovered)}
              {(annotationsByDay.get(hovered) ?? []).map((a) => (
                <div key={`${a.type}-${a.version}`} className="mt-1 text-gray-500 dark:text-gray-400">
                  {a.label}
                </div>
              ))}
              <div className="mt-1 text-indigo-600 dark:text-indigo-400">Click to open these runs</div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 max-h-72 overflow-auto">
          <table className="min-w-full text-xs" data-testid={`${testId}-table`}>
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-3 font-medium">Day</th>
                {series.map((s) => (
                  <th key={s.key} className="py-1 pr-3 text-right font-medium">
                    {s.label}
                  </th>
                ))}
                {tableExtra && <th className="py-1 pr-3 text-right font-medium">{tableExtra.header}</th>}
                <th className="py-1 font-medium">
                  <span className="sr-only">Runs</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {dates.map((date, i) => (
                <tr key={date} className="text-gray-700 dark:text-gray-300">
                  <td className="py-1 pr-3">
                    {formatDay(date)}
                    {(annotationsByDay.get(i) ?? []).map((a) => (
                      <span key={`${a.type}-${a.version}`} className="ml-2 text-gray-500 dark:text-gray-400">
                        {a.label}
                      </span>
                    ))}
                  </td>
                  {series.map((s) => (
                    <td key={s.key} className="py-1 pr-3 text-right tabular-nums">
                      {s.values[i] === null || s.values[i] === undefined ? '—' : formatValue(s.values[i] as number)}
                    </td>
                  ))}
                  {tableExtra && <td className="py-1 pr-3 text-right tabular-nums">{tableExtra.cell(i)}</td>}
                  <td className="py-1">
                    <Link to={dayLink(i)} className="text-indigo-600 hover:underline dark:text-indigo-400">
                      Runs
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {children}
    </section>
  );
}
